import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { DataTable } from '../../../../shared/components/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import {
  listMyWorkOrders,
  type WorkOrderSummary,
} from '../../api/brandingWorkOrderApi.js';
import { ReleasedOrdersTable } from '../vendors/ReleasedOrdersTable.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';

/** Mirrors backend VendorDashboardSnapshot (GET /v1/vendor/dashboard, §7). */
interface DashboardSnapshot {
  readonly vendorId: string;
  readonly generatedAt: string;
  readonly pendingAcceptance: number;
  readonly acceptedActive: number;
  readonly rejectedRecent: number;
  readonly painterPending: number;
  readonly executionInProgress: number;
  readonly overdue: number;
  readonly upcomingDeadlines: number;
  readonly slaViolations: number;
  readonly performanceScore: number | null;
  readonly workload: { readonly open: number; readonly capacity: number; readonly utilisationPct: number };
  readonly territorySummary: ReadonlyArray<{ readonly territoryId: string; readonly territoryName: string | null; readonly open: number }>;
  readonly workTypeSummary: ReadonlyArray<{ readonly workTypeId: string; readonly workTypeName: string | null; readonly open: number }>;
}

/** Mirrors backend InboxItem (GET /v1/vendor/inbox, VendorSelfService.inbox). */
interface InboxItem {
  /** Null for a line released to this vendor that he has not taken up yet. */
  readonly assignmentId: string | null;
  readonly requestLineId: string | null;
  readonly jobRequestId: string;
  readonly jobCode: string;
  readonly status: string;
  readonly priority: string;
  readonly territoryId: string;
  readonly acceptanceDueAt: string | null;
  readonly painterAssignmentStatus: string | null;
  readonly painterAssignmentActive: boolean;
  readonly painterUserId: string | null;
  readonly painterName: string | null;
  readonly painterCode: string | null;
  readonly painterMobileNumber: string | null;
  readonly painterRejectionReason: string | null;
  readonly painterRejectionRemarks: string | null;
  readonly painterRejectedAt: string | null;
}

const KPIS: ReadonlyArray<{ readonly key: keyof DashboardSnapshot; readonly labelKey: string }> = [
  { key: 'pendingAcceptance', labelKey: 'vops.dashboard.kpi.pendingAcceptance' },
  { key: 'acceptedActive', labelKey: 'vops.dashboard.kpi.acceptedActive' },
  { key: 'rejectedRecent', labelKey: 'vops.dashboard.kpi.rejectedRecent' },
  { key: 'painterPending', labelKey: 'vops.dashboard.kpi.painterPending' },
  { key: 'executionInProgress', labelKey: 'vops.dashboard.kpi.executionInProgress' },
  { key: 'overdue', labelKey: 'vops.dashboard.kpi.overdue' },
  { key: 'upcomingDeadlines', labelKey: 'vops.dashboard.kpi.upcomingDeadlines' },
  { key: 'slaViolations', labelKey: 'vops.dashboard.kpi.slaViolations' },
];

export function VendorDashboardPage(): ReactNode {
  const { number, money } = useBusinessNumber();
  const { token } = useAuth();
  const { t } = useTranslation();
  const { dayRange, at } = useBusinessDay();
  const [snap, setSnap] = useState<DashboardSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The vendor's Inbox opens this page, so the work he has been awarded is
  // shown here rather than only on a screen he has to go looking for. The API
  // takes the vendor from the session and returns approved orders only.
  const [orders, setOrders] = useState<readonly WorkOrderSummary[]>([]);
  // Same rule as the inbox below: an unread list is not "none approved".
  const [ordersError, setOrdersError] = useState<string | null>(null);
  // The awarded assignments themselves — the only rows that carry an
  // assignmentId, and so the only way to reach /vendor-ops/jobs/:assignmentId
  // by clicking. Work orders above are the commercial paper, not the job.
  const [inbox, setInbox] = useState<readonly InboxItem[] | null>(null);
  const [inboxError, setInboxError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    listMyWorkOrders(token)
      .then((r) => { if (alive) { setOrders(r.data); setOrdersError(null); } })
      .catch((e) => { if (alive) setOrdersError((e as Error).message); });
    return () => { alive = false; };
  }, [token]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await apiRequest<{ items: readonly InboxItem[] }>('/v1/vendor/inbox', { token });
        if (!Array.isArray(r.items)) throw new Error('Vendor inbox response did not contain an items list.');
        if (alive) { setInbox(r.items); setInboxError(null); }
      } catch (e) {
        if (alive) { setInbox(null); setInboxError((e as Error).message); }
      }
    })();
    return () => { alive = false; };
  }, [token]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await apiRequest<DashboardSnapshot>('/v1/vendor/dashboard', { token });
        if (alive) setSnap(r);
      } catch (e) {
        if (alive) setError((e as Error).message);
      }
    })();
    return () => {
      alive = false;
    };
  }, [token]);

  if (error) return <main aria-label="vendor-dashboard"><p role="alert">{error}</p></main>;
  if (!snap) return <main aria-label="vendor-dashboard"><p>{t('common.loading')}</p></main>;

  return (
    <main aria-label="vendor-dashboard">
      <h1>{t('vops.dashboard.title')}</h1>
      <p>{t('vops.dashboard.generated', { when: at(snap.generatedAt) })}</p>

      <nav aria-label="vendor-ops-nav">
        <Link to="/vendor-ops/clarifications" aria-label="nav-clarifications">{t('vops.dashboard.navClarifications')}</Link>
        {' · '}
        <Link to="/vendor-ops/performance" aria-label="nav-performance">{t('vops.dashboard.navPerformance')}</Link>
        {' · '}
        <Link to="/vendor-ops/sla" aria-label="nav-sla">{t('vops.dashboard.navSla')}</Link>
      </nav>

      <section aria-label="vendor-inbox">
        <h2>{t('vops.dashboard.inboxTitle')}</h2>
        {inboxError ? (
          <p role="alert" aria-label="inbox-error">{inboxError}</p>
        ) : inbox === null ? (
          <p aria-label="inbox-loading">{t('common.loading')}</p>
        ) : inbox.length === 0 ? (
          <p aria-label="inbox-empty">{t('vops.dashboard.inboxEmpty')}</p>
        ) : (
          <DataTable<InboxItem>
            testId="vendor-inbox-table"
            ariaLabel="vendor-inbox-table"
            rowKey={(i) => i.assignmentId ?? i.requestLineId ?? i.jobRequestId}
            rowAriaLabel={(i) => `inbox-${i.assignmentId ?? i.requestLineId}`}
            columns={[
              {
                key: 'job',
                header: t('vops.dashboard.colJob'),
                // A released line is taken up on Assign Job to Field Force.
                render: (i) => (
                  <Link
                    to={i.assignmentId ? `/vendor-ops/jobs/${i.assignmentId}` : '/vendor-ops/field-force'}
                    aria-label={`inbox-job-${i.assignmentId ?? i.requestLineId}`}
                  >
                    {i.jobCode || i.jobRequestId}
                  </Link>
                ),
              },
              { key: 'status', header: t('vops.common.status'), render: (i) => i.status },
              { key: 'priority', header: t('vops.common.priority'), render: (i) => i.priority || '—' },
              {
                key: 'due',
                header: t('vops.dashboard.colAcceptanceDue'),
                render: (i) =>
                  i.acceptanceDueAt ? at(i.acceptanceDueAt) : '—',
              },
              {
                key: 'painter',
                header: t('vops.dashboard.colPainter'),
                render: (i) =>
                  i.painterAssignmentActive && i.painterName
                    ? `${i.painterName}${i.painterCode ? ` · ${i.painterCode}` : ''}`
                    : t('vops.dashboard.painterUnassigned'),
              },
            ]}
            rows={inbox}
          />
        )}
      </section>

      <section aria-label="kpis">
        {KPIS.map((k) => (
          <div key={k.key} aria-label={`kpi-${k.key}`}>
            <span>{t(k.labelKey)}</span>
            <strong aria-label={`kpi-value-${k.key}`}>{String(snap[k.key])}</strong>
          </div>
        ))}
        <div aria-label="kpi-performanceScore">
          <span>{t('vops.dashboard.kpi.performanceScore')}</span>
          <strong aria-label="kpi-value-performanceScore">
            {snap.performanceScore === null ? '—' : number(snap.performanceScore)}
          </strong>
        </div>
      </section>

      <section aria-label="workload">
        <h2>{t('vops.common.workload')}</h2>
        <p>
          {t('vops.dashboard.wlOpen')} <strong aria-label="workload-open">{snap.workload.open}</strong> {t('vops.dashboard.wlCapacity')}{' '}
          <strong aria-label="workload-capacity">{snap.workload.capacity}</strong> ·{' '}
          <strong aria-label="workload-utilisation">{number(snap.workload.utilisationPct)}%</strong> {t('vops.dashboard.wlUtilised')}
        </p>
      </section>

      <section aria-label="my-work-orders">
        <h2>Approved work orders</h2>
        {ordersError ? (
          <p role="alert" aria-label="work-orders-error">{ordersError}</p>
        ) : orders.length === 0 ? (
          <p aria-label="work-orders-empty">No work order has been approved for you yet.</p>
        ) : (
          <DataTable<WorkOrderSummary>
            testId="my-work-orders-table"
            ariaLabel="my-work-orders-table"
            rowKey={(w) => w.workOrderId}
            rowAriaLabel={(w) => `work-order-${w.woNumber}`}
            columns={[
              { key: 'woNumber', header: 'Work order', render: (w) => w.woNumber },
              { key: 'subject', header: 'Subject', render: (w) => w.subject },
              { key: 'status', header: 'Status', render: (w) => w.woStatusCode },
              {
                key: 'valid',
                header: 'Valid',
                render: (w) => dayRange(w.validFromDate, w.validToDate),
              },
              { key: 'amount', header: 'Value', render: (w) => money(Number(w.totalAmount ?? '—')) },
            ]}
            rows={orders}
          />
        )}
      </section>

      {/* Branding orders released to this vendor. A branding order is a
          separate flow from a dealer job, so it never reaches the job inbox
          above; it is shown through the same table the vendor profile uses. */}
      <ReleasedOrdersTable
        titleKey="vops.dashboard.releasedOrders"
        emptyKey="vops.dashboard.releasedOrdersEmpty"
      />

      <section aria-label="territory-summary">
        <h2>{t('vops.dashboard.byTerritory')}</h2>
        {snap.territorySummary.length === 0 ? (
          <p aria-label="territory-empty">{t('vops.dashboard.noOpenJobs')}</p>
        ) : (
          <DataTable<{ readonly territoryId: string; readonly territoryName: string | null; readonly open: number }>
            testId="territory-table"
            ariaLabel="territory-table"
            rowKey={(ty) => ty.territoryId}
            rowAriaLabel={(ty) => `territory-${ty.territoryId}`}
            columns={[
              { key: 'territory', header: t('vops.dashboard.colTerritory'), render: (ty) => ty.territoryName ?? ty.territoryId },
              { key: 'open', header: t('vops.dashboard.colOpen'), render: (ty) => ty.open },
            ]}
            rows={snap.territorySummary}
          />
        )}
      </section>

      <section aria-label="worktype-summary">
        <h2>{t('vops.dashboard.byWorkType')}</h2>
        {snap.workTypeSummary.length === 0 ? (
          <p aria-label="worktype-empty">{t('vops.dashboard.noOpenJobs')}</p>
        ) : (
          <DataTable<{ readonly workTypeId: string; readonly workTypeName: string | null; readonly open: number }>
            testId="worktype-table"
            ariaLabel="worktype-table"
            rowKey={(w) => w.workTypeId}
            rowAriaLabel={(w) => `worktype-${w.workTypeId}`}
            columns={[
              { key: 'workType', header: t('vops.dashboard.colWorkType'), render: (w) => w.workTypeName ?? w.workTypeId },
              { key: 'open', header: t('vops.dashboard.colOpen'), render: (w) => w.open },
            ]}
            rows={snap.workTypeSummary}
          />
        )}
      </section>
    </main>
  );
}
